import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import { apiHelper, baseUrl } from "helpers/util";
import ColumnBox from "components/ColumnBox";
import RowBox from "components/RowBox";
import SmallHeading from "components/SmallHeading";
import { Alert, Snackbar } from "@mui/material";
import { Modal } from "antd";
import { getFolkLevelLocation } from "config/folkLevelConfig";

export default function AutoAttendance() {
    const currTime = new Date();
    const dateStr = currTime.toLocaleString("en-IN", { month: "long", day: "2-digit" });
    const timeStr = currTime.toLocaleString("en-IN", { hour12: true, hour: "2-digit", minute: "2-digit" });

    const printerProfile = {
        paperWidthPx: 384,
        paddingPx: 12,
        fontSizePx: 22,
        lineHeightPx: 34,
        logoWidthPx: 100,
        bleChunkSize: 128,
        bleWriteDelayMs: 60,
        bleRetryCount: 2,
        bleRetryDelayMs: 120,
        trailingFeedLines: 5,
        prePrintResetDelayMs: 250,
        postPrintResetDelayMs: 250,
        postPrintDrainMs: 1800,
    };

    const lateLevelsWithFine = ["FOLK Prelims", "FOLK Graduate", "FOLK Enhanced", "FOLK Plus"];
    const autoPrintLevels = ["FOLK Foundation", "FOLK Prelims", "FOLK Graduate", "FOLK Enhanced", "FOLK Plus"];
    const cutoff = currTime.getHours() > 16 || (currTime.getHours() === 16 && currTime.getMinutes() > 30);
    const getAndroidPrinterBridge = () => {
       if (typeof window === "undefined") {
            return null;
        }
        const bridge = window.AndroidApp;
        return bridge && typeof bridge.printPrasadam === "function" ? bridge : null;
    };

    const [mobileNo, setMobileNo] = useState("");
    const [getRecordId, setRecordId] = useState({});
    const [getDuplicateAttendance, setDuplicateAttendance] = useState({});
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("");
    const [isPrinterConnected, setIsPrinterConnected] = useState(() => Boolean(getAndroidPrinterBridge()));
    const [snackbarBottomOffset, setSnackbarBottomOffset] = useState(12);

    const formErrorState = useRef({});
    const printState = useRef({ printCharacteristic: null, index: 0, data: null });
    const searchTimeoutRef = useRef(null);
    const lastSearchedValueRef = useRef("");
    const isValidMobileNumber = (value) => /^\d{10}$/.test(String(value || "").trim());
    const isQrSearchValue = (value) => /^rec[A-Za-z0-9]{14}$/.test(String(value || "").trim());
    const restrictedLevels = ["GFY Registered", "AOMC Attended", "GFY Attended"];
    const showPageAlert = (text, severity = "error") => {
        setErrorMessage(text);
        setErrorColor(severity);
    };

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        const baseOffset = 12;
        const keyboardOpenThreshold = 80;

        const updateBottomOffset = () => {
            const viewport = window.visualViewport;
            if (!viewport) {
                setSnackbarBottomOffset(baseOffset);
                return;
            }

            const keyboardHeight = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
            setSnackbarBottomOffset(keyboardHeight > keyboardOpenThreshold ? keyboardHeight + baseOffset : baseOffset);
        };

        updateBottomOffset();

        const viewport = window.visualViewport;
        window.addEventListener("resize", updateBottomOffset);
        if (viewport) {
            viewport.addEventListener("resize", updateBottomOffset);
            viewport.addEventListener("scroll", updateBottomOffset);
        }

        return () => {
            window.removeEventListener("resize", updateBottomOffset);
            if (viewport) {
                viewport.removeEventListener("resize", updateBottomOffset);
                viewport.removeEventListener("scroll", updateBottomOffset);
            }
        };
    }, []);

    const resetForm = () => {
        setMobileNo("");
        setFetchedRecord(null);
        setErrorMessage(null);
        setErrorColor("");
        setRecordId({});
        setDuplicateAttendance({});
        setAreOptionsLoading(false);
        setIsFormSubmitting(false);
        formErrorState.current = {};
        printState.current = {
            ...printState.current,
            index: 0,
            data: null,
            canvas: null,
        };
        lastSearchedValueRef.current = "";
    };

    const searchByMobileNo = async (mobile) => {
        const searchValue = String(mobile || "").trim();
        if ((!isValidMobileNumber(searchValue) && !isQrSearchValue(searchValue)) || isFormSubmitting) {
            return;
        }

        try {
            setAreOptionsLoading(true);
            setErrorMessage(null);
            setFetchedRecord(null);
            lastSearchedValueRef.current = searchValue;

            const response = await apiHelper.get({
                url: `${baseUrl}/.netlify/functions/editable_sunday_attendance`,
                params: { search_text: searchValue },
            });

            if (response.status) {
                if (!response.json || response.json.length === 0) {
                    setErrorMessage("Oops! It looks like you haven't completed the registration yet. Kindly do so. Hare Krishna.");
                    setErrorColor("error");
                    return;
                }
                const record = response.json[0];
                const recordId = record["Record ID"];
                setRecordId(recordId);
                setDuplicateAttendance(record["Today attended ?"]);
                setFetchedRecord(record);

                if (Number(record["Count"]) > 2) {
                    showPageAlert("Warning: this attendee count is more than two.", "warning");
                }

                if (autoPrintLevels.includes(record["FOLK Level (Display)"])) {
                    await attemptPrintSlip(record, recordId, record["Today attended ?"] === 1);
                }
            } else {
                setErrorMessage(`API returned status false: ${JSON.stringify(response.json)}`);
                setErrorColor("error");
            }
        } catch (error) {
            console.error("Error in searchByMobileNo:", error);
            setErrorMessage("An error occurred while searching.");
            setErrorColor("error");
        } finally {
            setAreOptionsLoading(false);
        }
    };

    const requiresApproval = (record) => {
        const level = record["FOLK Level (Display)"];
        const count = Number(record["Count"]);
        return (restrictedLevels.includes(level) && count >= 2) || (!level && count >= 2);
    };

    const requestApprovalToContinue = () =>
        new Promise((resolve) => {
            showPageAlert("FOLK Divisional Head approval is required to mark attendance and print the slip.", "warning");
            Modal.confirm({
                title: "Approval Needed",
                content: "Do you have FOLK Divisional Head approval to continue?",
                okText: "Yes, continue",
                cancelText: "No",
                onOk: () => resolve(true),
                onCancel: () => {
                    showPageAlert("Approval denied. Cannot proceed.", "error");
                    resolve(false);
                },
            });
        });

    const attemptPrintSlip = async (record, recordId, isDuplicate = false) => {
        if (requiresApproval(record)) {
            const isApproved = await requestApprovalToContinue();
            if (!isApproved) {
                return;
            }
        }

        await handlePrintSlip(record, recordId, isDuplicate);
    };

    const handleMobileChange = (e) => {
        const val = e.target.value;
        setMobileNo(val);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const trimmedVal = val.trim();
        if (isValidMobileNumber(trimmedVal) || isQrSearchValue(trimmedVal)) {
            searchTimeoutRef.current = setTimeout(() => {
                if (lastSearchedValueRef.current !== trimmedVal) {
                    searchByMobileNo(trimmedVal);
                }
            }, 250);
        }
    };

    const ensurePrinterConnected = async () => {
        const androidPrinterBridge = getAndroidPrinterBridge();
        if (androidPrinterBridge) {
            setIsPrinterConnected(true);
            return true;
        }

        if (printState.current.printCharacteristic && printState.current.server && printState.current.server.connected) {
            setIsPrinterConnected(true);
            return true;
        }

        if (!navigator.bluetooth) {
            setIsPrinterConnected(false);
            showPageAlert("Web Bluetooth is not supported in this app/browser. Open this page in a BLE-supported browser to connect printer.", "error");
            return false;
        }

        showPageAlert("Searching for printer...", "info");
        try {
            const device = await navigator.bluetooth.requestDevice({ filters: [{ services: ["000018f0-0000-1000-8000-00805f9b34fb"] }] });
            showPageAlert(`Found: ${device.name || "Printer"}. Connecting...`, "info");
            const server = await device.gatt.connect();
            printState.current.server = server;
            const service = await server.getPrimaryService("000018f0-0000-1000-8000-00805f9b34fb");
            const characteristic = await service.getCharacteristic("00002af1-0000-1000-8000-00805f9b34fb");
            printState.current.printCharacteristic = characteristic;
            setIsPrinterConnected(true);
            showPageAlert("Printer connected successfully.", "success");
            return true;
        } catch (err) {
            console.log(err);
            printState.current.printCharacteristic = null;
            printState.current.server = null;
            setIsPrinterConnected(false);
            if (err && err.name === "NotFoundError") {
                showPageAlert("Printer selection cancelled.", "warning");
            } else {
                showPageAlert(`Bluetooth error: ${err.message}`, "error");
            }
            return false;
        }
    };

    const loadSlipLogo = () =>
        new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => resolve(null);
            image.src = "/images/folklogo.png";
        });

    const sleep = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

    const writeChunkToPrinter = async (chunk) => {
        const characteristic = printState.current.printCharacteristic;
        if (!characteristic) {
            throw new Error("Printer characteristic unavailable.");
        }

        if (typeof characteristic.writeValueWithResponse === "function") {
            await characteristic.writeValueWithResponse(chunk);
            return;
        }

        await characteristic.writeValue(chunk);
    };

    const resetBlePrinter = async () => {
        const ESC = 0x1b;
        const GS = 0x1d;
        const LF = 0x0a;

        // ESC @ init, ESC 2 default line spacing, ESC a 0 align left, then feed+cut and re-init.
        const resetSequence = new Uint8Array([
            ESC, 0x40,
            ESC, 0x32,
            ESC, 0x61, 0x00,
            LF, LF,
            GS, 0x56, 0x00,
            ESC, 0x40,
        ]);

        await writeChunkToPrinter(resetSequence);
    };

    const buildSlipPrintData = async (record, isLate, isLateWithFine, dStr, tStr, isDuplicate, outputType = "escpos") => {
        const ESC = 0x1b;
        const LF = 0x0a;
        const GS = 0x1d;
        const W = printerProfile.paperWidthPx;
        const PAD = printerProfile.paddingPx;
        const FS = printerProfile.fontSizePx;
        const LH = printerProfile.lineHeightPx;
        const BOLD = `bold ${FS}px Arial`;
        const NORMAL = `${FS}px Arial`;
        const SMALL = "18px Arial";

        const logoImg = await loadSlipLogo();
        const fields = [
            ["Name:", String(record["Name"] || record["HK Japa User Name"] || "")],
            ["F. Level:", String(record["FOLK Level (Display)"] || "")],
            ["Location:", String(getFolkLevelLocation(record["FOLK Level (Display)"]) || "")],
            ["A. Level:", String(record["Current Ashraya Level"] || "")],
            ["F. Guide:", String(record["Guide Name"] || "")],
            ["Guide No.:", String(record["Guide Mobile No."] || "")],
            ["Date:", String(dStr || "")],
            ["Time:", String(tStr || "").toUpperCase()],
        ];

        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        measureCtx.font = BOLD;
        const labelW = measureCtx.measureText("Guide No.:  ").width;
        const valW = W - PAD - labelW - PAD;

        const wrapLines = (ctx, text, maxW) => {
            const words = String(text || "").split(" ");
            const lines = [];
            let currentLine = "";

            for (const word of words) {
                const nextLine = currentLine ? `${currentLine} ${word}` : word;
                if (ctx.measureText(nextLine).width > maxW && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = nextLine;
                }
            }

            if (currentLine) {
                lines.push(currentLine);
            }

            return lines.length ? lines : [""];
        };

        measureCtx.font = NORMAL;
        const logoH = logoImg ? Math.round((logoImg.naturalHeight / logoImg.naturalWidth) * printerProfile.logoWidthPx) : 0;
        const fineLines = isLateWithFine ? 4 : 0;
        const duplicateHeaderLines = isDuplicate ? 1 : 0;

        let H = PAD + (logoImg ? logoH + 16 : 0) + LH + LH + 28
            + (isDuplicate ? duplicateHeaderLines * LH + 28 : 0)
            + (isLate ? LH + 28 : 0)
            + (isLateWithFine ? fineLines * LH + 28 : 0) + 16;

        for (const [, value] of fields) {
            H += wrapLines(measureCtx, value, valW).length * LH + 12;
        }

        H += 28 + LH + LH + 20 + LH + LH + LH * 2 + PAD;

        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "#000";
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1;

        let y = PAD;

        if (logoImg) {
            ctx.drawImage(logoImg, (W - printerProfile.logoWidthPx) / 2, y, printerProfile.logoWidthPx, logoH);
            y += logoH + 16;
        }

        ctx.font = BOLD;
        ctx.textAlign = "center";
        ctx.fillText("FOLK SUNDAY MEET-UP", W / 2, y + 32);
        y += LH;
        ctx.fillText("ATTENDANCE", W / 2, y + 32);
        y += LH;

        ctx.font = NORMAL;
        ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
        y += 28;

        if (isDuplicate) {
            ctx.font = "bold 26px Arial";
            ctx.fillText(isLate ? "DUPLICATE LATE ATTENDANCE" : "DUPLICATE ATTENDANCE", W / 2, y + 32);
            y += LH;
            ctx.font = NORMAL;
            ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
            y += 28;
        }

        if (isLate && !isDuplicate) {
            ctx.font = BOLD;
            ctx.fillText("LATE ATTENDANCE", W / 2, y + 32);
            y += LH;
            ctx.font = NORMAL;
            ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
            y += 28;
        }

        if (isLateWithFine) {
            ctx.setLineDash([8, 5]);
            ctx.strokeRect(PAD, y, W - PAD * 2, fineLines * LH + 14);
            ctx.setLineDash([]);
            ctx.font = SMALL;
            ctx.textAlign = "center";
            ctx.fillText("Please collect your", W / 2, y + 24);
            ctx.fillText("paid prasadam coupon (Rs. 50)", W / 2, y + 24 + LH);
            ctx.fillText("from the FOLK Front Desk", W / 2, y + 24 + LH * 2);
            ctx.fillText("after 7:00 PM.", W / 2, y + 24 + LH * 3);
            y += fineLines * LH + 28;
        }

        y += 16;
        ctx.textAlign = "left";
        for (const [label, value] of fields) {
            ctx.font = BOLD;
            ctx.fillText(label, PAD, y + FS);
            ctx.font = NORMAL;
            const lines = wrapLines(ctx, value, valW);
            lines.forEach((line, index) => {
                ctx.fillText(line, PAD + labelW, y + FS + index * LH);
            });
            y += lines.length * LH + 12;
        }

        ctx.font = NORMAL;
        ctx.textAlign = "center";
        ctx.fillText("- - - - - - - - - - - - - - - - - - - -", W / 2, y + 20);
        y += 28;
        ctx.font = BOLD;
        ctx.fillText("Hare Krishna !!", W / 2, y + 32);
        y += LH;
        ctx.fillText("Have a nice day", W / 2, y + 32);
        y += LH + 20;
        ctx.textAlign = "left";
        ctx.font = "600 34px Arial";
        ctx.fillText("Sign", PAD, y + 40);
        y += LH + 8;
        ctx.font = NORMAL;
        ctx.fillText("(FOLK Guide)", PAD, y + FS);
        y += LH * 3;

        if (outputType === "jpeg") {
            const jpegBase64 = canvas.toDataURL("image/jpeg", 0.95).split(";base64,")[1];
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;
            return jpegBase64;
        }

        const { data } = ctx.getImageData(0, 0, W, H);
        const bytesPerRow = Math.ceil(W / 8);
        const raster = [];

        for (let row = 0; row < H; row++) {
            for (let byteIndex = 0; byteIndex < bytesPerRow; byteIndex++) {
                let currentByte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    const pixelX = byteIndex * 8 + bit;
                    if (pixelX < W) {
                        const pixelIndex = (row * W + pixelX) * 4;
                        const luminance = data[pixelIndex] * 0.299 + data[pixelIndex + 1] * 0.587 + data[pixelIndex + 2] * 0.114;
                        if (luminance < 128) {
                            currentByte |= 0x80 >> bit;
                        }
                    }
                }
                raster.push(currentByte);
            }
        }

        const xL = bytesPerRow & 0xff;
        const xH = (bytesPerRow >> 8) & 0xff;
        const yL = H & 0xff;
        const yH = (H >> 8) & 0xff;

        const trailer = new Array(printerProfile.trailingFeedLines).fill(LF);
        const escPosData = new Uint8Array([
            ESC, 0x40,
            ESC, 0x32,
            ESC, 0x61, 0x01,
            GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...raster,
            ...trailer,
        ]);

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
        return escPosData;
    };

    const sendEscPosData = async (data) => {
        const CHUNK = printerProfile.bleChunkSize;
        const totalChunks = Math.ceil(data.length / CHUNK);
        showPageAlert(`Sending data to printer... (${data.length} bytes, ${totalChunks} chunks)`, "info");

        await resetBlePrinter();
        await sleep(printerProfile.prePrintResetDelayMs);

        for (let i = 0; i < data.length; i += CHUNK) {
            const chunk = data.slice(i, i + CHUNK);
            const chunkNo = Math.floor(i / CHUNK) + 1;
            let attempt = 0;

            while (attempt <= printerProfile.bleRetryCount) {
                try {
                    await writeChunkToPrinter(chunk);
                    if (i + CHUNK < data.length && printerProfile.bleWriteDelayMs > 0) {
                        await sleep(printerProfile.bleWriteDelayMs);
                    }
                    console.log(`Chunk ${chunkNo}/${totalChunks} sent OK`);
                    break;
                } catch (chunkErr) {
                    attempt += 1;
                    if (attempt > printerProfile.bleRetryCount) {
                        showPageAlert(`Failed at chunk ${chunkNo}/${totalChunks}: ${chunkErr.message}`, "error");
                        throw chunkErr;
                    }
                    await sleep(printerProfile.bleRetryDelayMs);
                }
            }
        }

        await sleep(printerProfile.bleWriteDelayMs * 2);
        await resetBlePrinter();
        await sleep(printerProfile.postPrintResetDelayMs);

        // Keep next print from starting while printer still drains internal buffer.
        const drainDelay = Math.max(printerProfile.postPrintDrainMs, totalChunks * 12);
        await sleep(drainDelay);
    };

    const onClickPrintSlip = async () => {
        if (!fetchedRecord) {
            showPageAlert("No record loaded. Search a mobile number first.", "error");
            return;
        }
        if (Number(fetchedRecord["Count"]) > 2) {
            showPageAlert("Warning: this attendee count is more than two.", "warning");
        }
        await attemptPrintSlip(fetchedRecord, getRecordId, getDuplicateAttendance === 1);
    };

    const handlePrintSlip = async (record, recordId, isDuplicate = false) => {
        if (isFormSubmitting) {
            showPageAlert("Previous print job is still in progress. Please wait.", "warning");
            return;
        }

        const isConnected = await ensurePrinterConnected();
        if (!isConnected) {
            return;
        }

        const androidPrinterBridge = getAndroidPrinterBridge();

        setIsFormSubmitting(true);
        const level = record["FOLK Level (Display)"];
        const isLate = cutoff;
        const isLateWithFine = cutoff && lateLevelsWithFine.includes(level);

        try {
            showPageAlert("Building print data...", "success");

            if (androidPrinterBridge) {
                const jpegBase64 = await buildSlipPrintData(record, isLate, isLateWithFine, dateStr, timeStr, isDuplicate, "jpeg");
                androidPrinterBridge.printPrasadam(jpegBase64);
                await sleep(printerProfile.postPrintDrainMs);
                setIsPrinterConnected(true);
                showPageAlert("Print sent successfully!", "success");
            } else {
                const escPosData = await buildSlipPrintData(record, isLate, isLateWithFine, dateStr, timeStr, isDuplicate);
                showPageAlert(`Print buffer ready (${escPosData.length} bytes). Sending to printer...`, "info");
                await sendEscPosData(escPosData);
                showPageAlert("Print sent successfully!", "success");
            }

            if (isDuplicate) {
                showPageAlert("Duplicate attendance detected. Slip printed without marking attendance.", "warning");
            } else {
                await markAttendance(recordId);
            }
        } catch (error) {
            console.error("Error in handlePrintSlip:", error);
            showPageAlert(`Print failed: ${error.message || error}`, "error");
        } finally {
            setIsFormSubmitting(false);
            resetForm();
        }
    };

    const markAttendance = async (recordId) => {
        const payload = { record_id: Array.isArray(recordId) ? recordId[0] : recordId };
        try {
            const response = await apiHelper.post({
                url: `${baseUrl}/.netlify/functions/marked_attendance`,
                data: payload,
            });
            if (response.status) {
                showPageAlert("Attendance recorded successfully!", "success");
                resetForm();
            } else {
                showPageAlert("Failed to record attendance. Please try again.", "error");
            }
        } catch (error) {
            showPageAlert("Something went wrong while recording attendance.", "error");
        }
    };

    const isLateUI = fetchedRecord && cutoff;
    const isLateWithFineUI = fetchedRecord && cutoff && lateLevelsWithFine.includes(fetchedRecord["FOLK Level (Display)"]);
    const isManualLevel = fetchedRecord && !autoPrintLevels.includes(fetchedRecord["FOLK Level (Display)"]);
    const isDuplicateUI = fetchedRecord && getDuplicateAttendance === 1;
    const shouldShowPrintButton = Boolean(fetchedRecord) && isManualLevel;

    return (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "15px", width: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
            <ColumnBox sx={{ alignItems: "center", width: "100%" }}>
                <SmallHeading
                    sx={{
                        alignSelf: "center",
                        textAlign: "center",
                        whiteSpace: "normal",
                        width: "100%",
                        fontSize: { xs: "1.8rem", sm: "2rem" },
                        lineHeight: 1.2,
                    }}
                >
                    FOLK SUNDAY Program
                </SmallHeading>
                <ColumnBox sx={{ alignItems: "center", maxWidth: "500px", width: "100%" }}>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "330px", sm: "390px" },
                            marginTop: "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: { xs: "6px", sm: "10px" },
                            mx: "auto",
                            px: "4px",
                            boxSizing: "border-box",
                        }}
                    >
                        <TextField
                            required
                            label="Mobile No. / Scan QR"
                            autoFocus
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={mobileNo}
                            onChange={handleMobileChange}
                            sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "24px", flexShrink: 0 }}>
                            {areOptionsLoading ? <CircularProgress size="1.5rem" /> : <MobileFriendlyIcon size="1.5rem" />}
                        </Box>
                    </Box>

                    {fetchedRecord && shouldShowPrintButton && (
                        <div
                            id="print-slip"
                            style={{
                                marginTop: "20px",
                                width: "72mm",
                                background: "#ffffff",
                                border: "2px solid #333",
                                borderRadius: "2px",
                                padding: "4mm 5mm 6mm",
                                fontFamily: "'Courier New', Courier, monospace",
                                fontSize: "4.5mm",
                                color: "#111",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                lineHeight: "1.4",
                            }}
                        >
                            <div style={{ textAlign: "center", marginBottom: "3mm" }}>
                                <img src="/images/folklogo.png" alt="FOLK Logo" style={{ width: "16mm", display: "block", margin: "0 auto 1.5mm" }} />
                                <div style={{ fontWeight: 900, fontSize: "7.5mm", letterSpacing: "0.5px" }}>FOLK SUNDAY</div>
                                <div style={{ fontWeight: 900, fontSize: "7.5mm", letterSpacing: "0.5px", marginTop: "0.5mm" }}>MEET-UP</div>
                                <div style={{ fontWeight: 700, fontSize: "6.5mm", marginTop: "1mm" }}>ATTENDANCE SLIP</div>
                            </div>

                            <div style={{ borderTop: "1px solid #333", borderBottom: "1px solid #333", margin: "2mm 0", padding: "1.5mm 0" }} />

                            {isDuplicateUI && (
                                <>
                                    <div style={{ textAlign: "center", fontWeight: 900, fontSize: "6mm", margin: "2mm 0", padding: "1.5mm", backgroundColor: "#fff3cd", borderRadius: "1mm" }}>
                                        {isLateUI ? "⚠ DUPLICATE LATE" : "⚠ DUPLICATE"}
                                    </div>
                                    <div style={{ borderTop: "1px dashed #666", margin: "2mm 0" }} />
                                </>
                            )}

                            {isLateUI && !isDuplicateUI && (
                                <>
                                    <div style={{ textAlign: "center", fontWeight: 900, fontSize: "6.5mm", margin: "2mm 0", padding: "1.5mm", backgroundColor: "#ffe5e5", borderRadius: "1mm" }}>
                                        ⏰ LATE ATTENDANCE
                                    </div>
                                    <div style={{ borderTop: "1px dashed #666", margin: "2mm 0" }} />
                                </>
                            )}

                            {isLateWithFineUI && (
                                <div style={{ border: "2px solid #d32f2f", padding: "2mm", textAlign: "center", fontSize: "5.5mm", fontWeight: 700, marginBottom: "2mm", lineHeight: "1.8", backgroundColor: "#ffebee", borderRadius: "1mm" }}>
                                    PRASADAM COUPON: Rs. 50<br />
                                    <span style={{ fontSize: "5mm" }}>Collect from FOLK Desk after 7:00 PM</span>
                                </div>
                            )}

                            <div style={{ margin: "2mm 0" }}>
                                {[
                                    ["Name:", fetchedRecord["Name"] || fetchedRecord["HK Japa User Name"] || ""],
                                    ["F. Level:", fetchedRecord["FOLK Level (Display)"] || ""],
                                    ["Location:", getFolkLevelLocation(fetchedRecord["FOLK Level (Display)"]) || ""],
                                    ["A. Level:", fetchedRecord["Current Ashraya Level"] || ""],
                                    ["F. Guide:", fetchedRecord["Guide Name"] || ""],
                                    ["Guide No.:", fetchedRecord["Guide Mobile No."] || ""],
                                    ["Count:", String(fetchedRecord["Count"] || "0")],
                                    ["Date:", dateStr],
                                    ["Time:", timeStr.toUpperCase()],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", gap: "1.5mm", marginBottom: "1.5mm", fontSize: "5mm", wordBreak: "break-word" }}>
                                        <span style={{ fontWeight: 900, minWidth: "18mm", whiteSpace: "nowrap" }}>{label}</span>
                                        <span style={{ flex: 1 }}>{value}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ borderTop: "1px solid #333", borderBottom: "1px solid #333", margin: "2mm 0", padding: "1.5mm 0" }} />

                            <div style={{ textAlign: "center", fontWeight: 700, fontSize: "6.5mm", lineHeight: "2", margin: "2mm 0", color: "#1a5c1a" }}>
                                <div>🙏 Hare Krishna 🙏</div>
                                <div style={{ fontSize: "5.5mm", fontWeight: 600 }}>Have a wonderful day</div>
                            </div>

                            <div style={{ marginTop: "3mm", textAlign: "left", paddingLeft: "1.5mm", borderTop: "1px solid #999", paddingTop: "2mm" }}>
                                <div style={{ fontWeight: 700, fontSize: "7mm", marginBottom: "1mm" }}>____________</div>
                                <div style={{ fontSize: "5mm" }}>Guide Signature</div>
                            </div>
                        </div>
                    )}

                    {errorMessage && (
                        <Snackbar
                            open={!!errorMessage}
                            autoHideDuration={8000}
                            onClose={() => setErrorMessage(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            sx={{
                                width: "min(500px, 90vw)",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bottom: `calc(${snackbarBottomOffset}px + env(safe-area-inset-bottom))`,
                            }}
                        >
                            <Alert severity={errorColor || "error"} onClose={() => setErrorMessage(null)} sx={{ width: "100%", minWidth: 0, alignItems: "center", fontSize: "1rem", padding: "12px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
                                {errorMessage}
                            </Alert>
                        </Snackbar>
                    )}

                    <RowBox sx={{ width: "100%", marginTop: "15px", alignItems: "center", justifyContent: "space-around" }}>
                        {shouldShowPrintButton && (
                            <Button sx={{ marginTop: "15px" }} onClick={onClickPrintSlip} variant="outlined" disabled={isFormSubmitting}>
                                {isFormSubmitting ? "Printing..." : "Print Slip"}
                            </Button>
                        )}
                    </RowBox>
                </ColumnBox>
            </ColumnBox>
        </Box>
    );
}
